import { apiClient } from '../api/apiClient';

export interface DoctorProfile {
  id: number;
  title?: string | null;
  first_name?: string | null;
  middle_name?: string | null;
  last_name?: string | null;
  full_name?: string | null;
  email?: string | null;
  /** From API as contact_number; use phone_number ?? contact_number for display */
  phone_number?: string | null;
  contact_number?: string | null;
  alternate_contact_number?: string | null;
  date_of_birth?: string | null;
  gender?: string | null;
  country_code_id?: number | null;
  timezone?: string | null;
  timezone_id?: number | null;
  timezone_name?: string | null;
  profile_photo_url?: string | null;
  /** Stable cacheable URL for header avatar (e.g. /api/v1/doctors/profile/me/avatar?v=...) */
  avatar_url?: string | null;
  bio?: string | null;
  // Extended fields used in profile cards
  years_experience?: number | null;
  registration_number?: string | null;
  registration_council?: string | null;
  registration_year?: number | null;
  specialization_name?: string | null;
  subspecialization_name?: string | null;
  country?: string | null;
  city?: string | null;
  pincode?: string | null;
  address_line1?: string | null;
  address_line2?: string | null;
  state?: string | null;
  languages_spoken?: string[] | null;
}

export interface DoctorProfileUpdate {
  title?: string | null;
  first_name?: string;
  middle_name?: string | null;
  last_name?: string;
  phone_number?: string | null;
  country_code_id?: number | null;
  timezone_id?: number | null;
  profile_photo_url?: string | null;
  bio?: string | null;
  date_of_birth?: string | null;
  gender?: string | null;
  address_line1?: string | null;
  address_line2?: string | null;
  city?: string | null;
  state?: string | null;
  country?: string | null;
  pincode?: string | null;
}

export interface DoctorEducation {
  id: number;
  degree_type: string;
  degree_name: string;
  institution_name: string;
  university_name: string;
  year_of_completion: number;
  certificate_document_url?: string;
  specialization_id?: number | null;
  subspecialization_id?: number | null;
  specialization_name?: string | null;
  subspecialization_name?: string | null;
}

export interface DoctorCertification {
  id: number;
  certification_type_id?: number;
  certification_name: string;
  issuing_organization: string;
  certificate_number?: string | null;
  issue_date: number;
  expiry_date?: number | null;
  is_expired?: boolean;
  certificate_document_url?: string | null;
  specialization_id?: number | null;
  subspecialization_id?: number | null;
  specialization_name?: string | null;
  subspecialization_name?: string | null;
}

export interface DoctorDocument {
  id: number;
  document_type: string;
  document_subtype?: string | null;
  document_url: string;
  created_at: string;
}

export interface DoctorPracticeTiming {
  id: number;
  day_of_week: string;
  is_available: boolean;
  start_time: string;
  end_time: string;
  slot_duration: number;
  breaks?: Array<{ break_start_time: string; break_end_time: string }>;
}

export interface DoctorPractice {
  id: number;
  practice_name: string;
  practice_type: string;
  address_line1: string;
  address_line2?: string | null;
  city: string;
  state: string;
  postal_code: string;
  country: string;
  contact_number: string;
  website_link?: string | null;
  contact_email?: string | null;
  is_primary: boolean;
  timings?: DoctorPracticeTiming[];
}

export interface DoctorBankDetails {
  id: number;
  account_holder_name: string;
  masked_account_number: string;
  ifsc_code: string;
  bank_name?: string | null;
  branch_name?: string | null;
  account_type: string;
  upi_id?: string | null;
  pan_number?: string | null;
  gst_number?: string | null;
}

export interface DoctorService {
  id: number;
  doctor_id: number;
  service_id?: number | null;
  custom_service_name?: string | null;
  service_name?: string | null;
  effective_service_name: string;
  service_description?: string | null;
  service_fee?: number | null;
  duration?: number | null;
  is_active: boolean;
  service_category_name?: string | null;
  display_order: number;
  created_at: string;
  updated_at?: string | null;
}

export interface CountryCode {
  id: number;
  country_name: string;
  dial_code: string;
  min_digits?: number;
  max_digits?: number;
}

export interface Timezone {
  id: number;
  identifier: string;
  display_name: string;
  utc_offset: string;
  is_active: boolean;
}

class ProfileService {
  // GET /api/v1/doctors/profile/profile
  async getProfile(): Promise<DoctorProfile> {
    const response = await apiClient.get<DoctorProfile>('/api/v1/doctors/profile/profile');
    return response.data;
  }

  // PATCH /api/v1/doctors/profile/profile
  async updateProfile(payload: DoctorProfileUpdate): Promise<DoctorProfile> {
    const response = await apiClient.patch<DoctorProfile>('/api/v1/doctors/profile/profile', payload);
    return response.data;
  }

  async getEducation(): Promise<DoctorEducation[]> {
    const response = await apiClient.get<DoctorEducation[]>('/api/v1/doctors/profile/education');
    return response.data;
  }

  async getCertifications(): Promise<DoctorCertification[]> {
    const response = await apiClient.get<DoctorCertification[]>('/api/v1/doctors/profile/certifications');
    return response.data;
  }

  async getDocuments(): Promise<DoctorDocument[]> {
    const response = await apiClient.get<DoctorDocument[]>('/api/v1/doctors/profile/documents');
    return response.data;
  }

  async getDocumentDownloadUrl(documentId: number): Promise<string> {
    const response = await apiClient.get<{
      download_url: string;
    }>(`/api/v1/doctors/profile/documents/${documentId}/download`);
    return response.data.download_url;
  }

  /**
   * Returns a URL that can be opened in the browser for viewing a file.
   * For s3:// URIs, fetches a presigned GET URL from the backend.
   * For http(s) or /files/ URIs, returns as-is.
   */
  async getPresignedViewUrl(fileUri: string): Promise<string> {
    const uri = (fileUri || '').trim();
    if (uri.startsWith('http://') || uri.startsWith('https://')) {
      return uri;
    }
    if (uri.startsWith('/files/')) {
      return uri;
    }
    if (uri.startsWith('s3://')) {
      const response = await apiClient.get<{ url: string }>('/api/v1/files/s3presign', {
        params: { s3_uri: uri, format: 'url', expires_in: 3600 },
      });
      return response.data.url;
    }
    return uri;
  }

  async getPractices(): Promise<DoctorPractice[]> {
    const response = await apiClient.get<DoctorPractice[]>('/api/v1/doctors/profile/practices');
    return response.data;
  }

  async getBankDetails(): Promise<DoctorBankDetails | null> {
    try {
      const response = await apiClient.get<DoctorBankDetails>('/api/v1/doctors/profile/bank-details');
      return response.data;
    } catch (e: any) {
      if (e?.response?.status === 404) {
        return null;
      }
      throw e;
    }
  }

  async getServices(): Promise<DoctorService[]> {
    const response = await apiClient.get<DoctorService[]>('/api/v1/doctors/profile/services');
    return response.data;
  }

  async getCountryCodes(): Promise<CountryCode[]> {
    // Use trailing slash to avoid 307 redirect from FastAPI
    const response = await apiClient.get<CountryCode[]>('/api/v1/country-codes/');
    return response.data;
  }

  async getTimezones(): Promise<Timezone[]> {
    const response = await apiClient.get<Timezone[]>('/api/v1/timezones/');
    return response.data;
  }

  async getNotificationSettings(): Promise<{ notifications_enabled: boolean }> {
    const response = await apiClient.get<{ notifications_enabled: boolean }>('/api/v1/notifications/doctors/settings');
    return response.data;
  }

  async updateNotificationSettings(notifications_enabled: boolean): Promise<{ notifications_enabled: boolean }> {
    const response = await apiClient.put<{ notifications_enabled: boolean }>('/api/v1/notifications/doctors/settings', {
      notifications_enabled,
    });
    return response.data;
  }
}

export const profileService = new ProfileService();

